/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Ring2All Team
 📄 File: auditLogApi.ts
 🗓️ Created: 2025-12-02
 🧩 Description:
    API client functions for Audit Logs module
────────────────────────────────────────────────────────────────*/

import { buildApiUrl, getApiHeaders } from '../../../config/api';
import { ListAuditLogsParams, ListAuditLogsResponse } from '../types';

const AUDIT_LOGS_ENDPOINT = '/api/audit-logs';

/**
 * Fetch list of audit logs
 */
export async function fetchAuditLogs(
  params: ListAuditLogsParams
): Promise<ListAuditLogsResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.page !== undefined) queryParams.append('page', String(params.page));
  if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params.action) queryParams.append('action', params.action);
  if (params.resource) queryParams.append('resource', params.resource);
  if (params.userId) queryParams.append('userId', params.userId);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);

  const url = buildApiUrl(`${AUDIT_LOGS_ENDPOINT}?${queryParams.toString()}`);
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch audit logs' }));
    throw new Error(error.message || 'Failed to fetch audit logs');
  }

  return response.json();
}

/**
 * Fetch current user's audit logs
 */
export async function fetchMyAuditLogs(
  params: ListAuditLogsParams
): Promise<ListAuditLogsResponse> {
  const queryParams = new URLSearchParams();
  
  if (params.page !== undefined) queryParams.append('page', String(params.page));
  if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params.action) queryParams.append('action', params.action);
  if (params.resource) queryParams.append('resource', params.resource);
  if (params.startDate) queryParams.append('startDate', params.startDate);
  if (params.endDate) queryParams.append('endDate', params.endDate);

  const url = buildApiUrl(`${AUDIT_LOGS_ENDPOINT}/me?${queryParams.toString()}`);
  const response = await fetch(url, {
    method: 'GET',
    headers: getApiHeaders(),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Failed to fetch audit logs' }));
    throw new Error(error.message || 'Failed to fetch audit logs');
  }

  return response.json();
}

